import { pipe } from "fp-ts/function";
import { option } from "fp-ts";
import {
  Body,
  Box,
  Children,
  Heading,
  Space,
  Stack,
  TextChildren,
} from "@dbn/omnichannel-design-system-g4p";
import * as classes from "./PackagesSelection.css";
import cx from "classnames";

type PackagePriceProps = {
  amountIntPart: TextChildren;
  amountDecPart?: TextChildren;
  currency: TextChildren;
  info: option.Option<TextChildren>;
  tooltip: option.Option<Children>;
  periodicity: TextChildren;
  before_discount_amount_int_part?: TextChildren;
  before_discount_amount_dec_part?: TextChildren;
  before_discount_currency?: TextChildren;
};

export const PackagePrice = ({
  amountIntPart,
  amountDecPart,
  currency,
  info,
  tooltip,
  periodicity,
  before_discount_amount_int_part,
  before_discount_amount_dec_part,
  before_discount_currency,
}: PackagePriceProps) => {
  const orFreeBlock = pipe(
    info,
    option.map(l =>
      l.toString().trim().length > 0 ? (
        <Box vAlignContent="center">
          <Body weight="regular" size="medium">
            {l}
          </Body>
        </Box>
      ) : null
    ),
    option.toNullable
  );

  return (
    <Box column grow vAlignContent="bottom" hAlignContent="center">
      <Stack vAlignContent="bottom" units={2}>
        {before_discount_amount_int_part && (
          <Stack
            vAlignContent="bottom"
            className={classes.priceBeforeDiscountAmount}
          >
            <Box className={classes.priceBeforeDiscountAmountStrike} />
            <Heading
              size="medium"
              weight="medium"
              className={classes.priceBeforeDiscountLabel}
            >
              {before_discount_amount_int_part}
            </Heading>
            {before_discount_amount_dec_part && (
              <Heading
                size="small"
                weight="medium"
                className={classes.priceBeforeDiscountLabel}
              >
                {before_discount_amount_dec_part}
              </Heading>
            )}
            <Space units={2} />
            <Heading
              size="small"
              weight="medium"
              className={classes.priceBeforeDiscountLabel}
            >
              {pipe(
                before_discount_currency,
                option.fromNullable,
                option.fold(
                  () => currency,
                  c => c
                )
              )}
            </Heading>
          </Stack>
        )}

        <Box
          style={{ alignItems: "baseline", flexWrap: "wrap", maxWidth: "100%" }}
        >
          <Box
            style={{
              justifyContent: "center",
              display: "inline",
              alignItems: "center",
            }}
          >
            <Heading size="medium" weight="medium">
              {amountIntPart}
            </Heading>
            {amountDecPart && (
              <Heading size="small" weight="medium">
                {amountDecPart}
              </Heading>
            )}
          </Box>
          <Space units={2} />
          <Box
            style={{
              justifyContent: "center",
              flex: "1 1 auto",
              alignItems: "center",
            }}
          >
            <Heading size="small" weight="medium">
              {currency}
            </Heading>
          </Box>
        </Box>

        {!before_discount_amount_int_part && orFreeBlock}
      </Stack>

      {before_discount_amount_dec_part && orFreeBlock}

      <span className={cx(classes.pricePeriodicity)}>
        {periodicity}
        &nbsp;
        {pipe(tooltip, option.toNullable)}
      </span>
    </Box>
  );
};
